import { useState, useEffect, useRef } from 'react';
import { secondsLeftToAddListingToCountdown, secondsInAHour } from '@Components/Utilities/SandhillsConstants';
import TranslationText from '@Components/Utilities/TranslationText';
import CurrencySelector from '@Components/Shared/CurrencySelector';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import * as CurrencyFormatter from '@Components/Utilities/CurrencyFormatter';
import RentalAndLeasePrices from '@Components/Shared/RentalAndLeasePrices';
import Countdown from 'react-countdown';
import { useRecoilValue } from 'recoil';
import atoms from '@Components/Utilities/SandhillsAtoms';

const MainDetailPricing = (props) => {

    const [currencyCode, setCurrencyCode] = useState(props.CurrencySelector.DefaultCurrency.toUpperCase());    

    const [showAuctionDate, setShowAuctionDate] = useState(true);

    const windowSize = useWindowSize();

    const [isTimerActive, setIsTimerActive] = useState(true);
    const currentSite = useRecoilValue(atoms.currentWebsite);            
        
    let countdownTimerLastTimeRemainingDefaultValue = -63072001;    
       
    const countdownTimerLastTimeRemaining = useRef(countdownTimerLastTimeRemainingDefaultValue);  //Use this to keep track of current time for AT listing. Initialize with 2 years and 1 day worth of seconds.
        

    const getDropdownId = (priceType) => {
        let id = '';
        let mobile = props.isMobile ? '-mobile' : '';

        switch (priceType) {
            case 'exclusive':
                id = `exclusive-wholesale-currency${mobile}`;
                break;
            case 'alternate':
                id = `alternate-retail-currency${mobile}`;
                break;
            default:
                id = `retail-currency${mobile}`;
        }

        return id;
    };
        
    const hasAdditionalPricing = () => {
        return (props.ShowPreAuctionTime && !props.ShowWholesalePriceOnly) ||
            (props.ShowWholeSale && !props.IsExclusiveWholeSale) ||
            (props.PriceGroup?.length > 0);
    };

    const formatVariantPrice = (defaultCurrencyCode) => {
        return (<>{` ${defaultCurrencyCode ? defaultCurrencyCode : currencyCode} ${CurrencyFormatter.FormatCurrency(props.variant?.Price, props.CultureCode, defaultCurrencyCode ? defaultCurrencyCode : currencyCode, 0, 0)}`}</>);
    };

    const addListing = (list, listing, secondsRemaining) => {
        const exists = list.some(item => item.ListingID === listing.ListingID);
        if (!exists) {
            props.updateAuctionBiddingDataSecondsRemaining(secondsRemaining, props);  //Hook for updating time remaining before this gets re-rendered.
            return [...list, listing];
        }
        return list;
    };
        
    const addListingToCountdownListings = (secondsRemaining) => {
        props.setCountdownListings(prevList => addListing(prevList, props, secondsRemaining));        
    }

    const handleTick = (timeRemainingInMilliseconds) => {  

        let timerRemainingFromCountDown = Math.floor(timeRemainingInMilliseconds / 1000);               
        
        if (currentSite.IsAuctionTimeAuctionSite && props.auctionDetails.IsAT && !props.isPrintPage) {
                        
            if (countdownTimerLastTimeRemaining.current > secondsInAHour) {
                if (timerRemainingFromCountDown <= secondsInAHour) {
                    setShowAuctionDate(false);
                    props.auctionListingNowHasUnder1HourLeft(timerRemainingFromCountDown);
                }
                if (props.forceAddToCountdownListings == true) { 
                    addListingToCountdownListings(timerRemainingFromCountDown);
                }
            } else if ((props.forceAddToCountdownListings == true && timerRemainingFromCountDown > -11) || ((countdownTimerLastTimeRemaining.current == countdownTimerLastTimeRemainingDefaultValue || countdownTimerLastTimeRemaining.current > secondsLeftToAddListingToCountdown) && timerRemainingFromCountDown <= secondsLeftToAddListingToCountdown && isTimerActive)) {                
                addListingToCountdownListings(timerRemainingFromCountDown);

            } else if (countdownTimerLastTimeRemaining.current <= -10 && timerRemainingFromCountDown <= -10) {
                // Details page should refresh once we get to the end of an auciton, so just let these requests continue until that happens
                setIsTimerActive(false)                
            } 
        }

        //Check if this value has been set by countdown yet
        countdownTimerLastTimeRemaining.current = timerRemainingFromCountDown; 
    };

    //Countdown renderer for AT listings
    const countDownRenderer = ({ days, hours, minutes, seconds, completed }) => {
        if (completed || seconds < 0) {
            return <TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionhasended' />;
        } else {

            if (seconds > 0 && minutes > 5) minutes += 1;
            const isUnderOneHour = days === 0 && hours === 0 && minutes <= 60;
            const formatNumber = (number, isRed) => (
                <strong className={isRed ? 'red-text' : ''}>{number}</strong>
            );

            return (
                <span>
                    {days > 0 ? (
                        <>
                            {formatNumber(days, false)}{' '}
                            <span>{days === 1 ? translator.translate('sandhills.day') : translator.translate('sandhills.days')}</span>{' '}
                            {hours > 0 && (
                                <>
                                    {formatNumber(hours, false)}{' '}
                                    <span>{hours === 1 ? translator.translate('sandhills.hour') : translator.translate('sandhills.hours')}</span>
                                </>
                            )}
                        </>
                    ) : hours > 0 ? (
                        <>
                            {formatNumber(hours, isUnderOneHour)}{' '}
                            <span>{hours === 1 ? translator.translate('sandhills.hour') : translator.translate('sandhills.hours')}</span>{' '}
                            {minutes > 0 && (
                                <>
                                    {formatNumber(minutes, isUnderOneHour)}{' '}
                                    <span>min</span>
                                </>
                            )}
                        </>
                    ) : minutes > 0 ? (
                        <>
                            {formatNumber(minutes, isUnderOneHour)}{' '}
                            <span>min</span>{' '}
                            {minutes < 7 && hours === 0 && (
                                <>
                                    {formatNumber(seconds, isUnderOneHour)}{' '}
                                    <span>{seconds === 1 ? translator.translate('sandhills.second') : translator.translate('sandhills.seconds')}</span>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {formatNumber(seconds, isUnderOneHour)}{' '}
                            <span>{seconds === 1 ? translator.translate('sandhills.second') : translator.translate('sandhills.seconds')}</span>
                        </>
                    )}
                </span>
            );
        }
    };

    const showAuctionData = () => {
        const hasBiddingData = props.auctionBiddingData && props.auctionDetails;
        // If no bidding data, return now
        if (!hasBiddingData) {
            return false;
        }

        // As of right now only AT will have the data populated on results, but if it is a result we do not want to show this section
        if (props.auctionDetails.IsAT) {
            return !props.auctionDetails.IsAuctionResult;
        }

        // Default
        return true;
    }

    return (
        <div className='listing-prices'>
            {showAuctionData() ?
                <>
                    <div className='listing-prices__current-bid'>
                        <span className='listing-prices__bid-text'>{props.auctionBiddingData.BidLabel}:</span>
                        <div className={!props.auctionDetails.IsAuctionResult ? 'currency-selector column' : 'currency-selector'}>
                            <span className='listing-prices__bid-formatted'>{props.auctionBiddingData.CurrentBidFormatted}</span>
                            {props.auctionDetails?.QuantityLabel && <span className='listing-prices__quantity-label'>{props.auctionDetails?.QuantityLabel}</span>}

                        </div>
                        {props.AverageWeight &&
                            <div>
                                <span className='listing-prices__quantity-label'><TranslationText tKey='Sandhills.TradeSites.MVC.BaseWeight' />: </span> {props.AverageWeight}
                            </div>
                        }
                        {props.CalcPricePerHead &&
                            <div>
                                <span className='listing-prices__quantity-label'><TranslationText tKey='Sandhills.CalculatedPricePerHead' />: </span>{props.CalcPricePerHead}
                            </div>
                        }
                        {props.SlidePrice && props.SlidePriceLabel &&
                            <div>
                                <span className='listing-prices__quantity-label'>{props.SlidePriceLabel}: </span>{props.SlidePrice}
                            </div>
                        }
                        {props.WeightStop &&
                            <div>
                                <span className='listing-prices__quantity-label'><TranslationText tKey='Sandhills.WeightStop' />: </span>{props.WeightStop}
                            </div>
                        }
                    </div>
                    <div className='listing-prices_auction-info'>
                        {!props.auctionDetails.IsLiveEvent &&
                            <div className='' dangerouslySetInnerHTML={{ __html: props.auctionBiddingData.LiveBiddingOpens }} />
                        }
                        {props.auctionDetails.AuctionDate && !props.auctionDetails.IsAuctionResult && showAuctionDate &&
                            <div className='sale'>
                                <strong>{props.auctionDetails.AuctionDateLabel}: </strong>
                                <span className='detail__sale-ends'>{props.auctionDetails.AuctionDate}</span>
                            </div>}
                        {props.EstimatedTimeDisplay && props.EstimatedTimeDisplay != "" && (
                            <div className='estimated-time-remaining'>
                                <strong>{translator.translate('sandhills.estimatedtimeremaining')}: </strong>
                                <span>{props.EstimatedTimeDisplay}</span>
                            </div>
                        ) || <div>&nbsp;</div>}
                        {!props.auctionBiddingData.IsAllBiddingPaused && !props.auctionBiddingData.IsListingPaused && props.auctionBiddingData.TimeLeft && props.auctionDetails.IsLiveEvent && (
                            <div className='time-remaining'>
                                <strong><span><TranslationText tKey='sandhills.timeremaining' />: </span></strong>
                                {isTimerActive ? (
                                    <>
                                        
                                        <span>                                            
                                            <Countdown date={props.auctionBiddingData.ProjectedEndDateTime} renderer={countDownRenderer} onTick={({ total }) => handleTick(total)} overtime={true}
                                            />
                                        </span>
                                    </>
                                ) : (

                                    <div><span dangerouslySetInnerHTML={{ __html: props.auctionBiddingData?.TimeLeft }} /></div>

                                )}
                            </div>
                        ) || <div>&nbsp;</div>}
                        {props.timeExtended &&
                            <div className='time-extended'>  *<TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionextendedtext' /> </div>
                        }
                    </div>

                </>
                :
                <>
                    {props.IsRY ?
                        <>
                            <div className='listing-prices__main'>
                                {props.RetailPrice && !props.HideCurrencySelector && windowSize.isMobile &&
                                    <CurrencySelector
                                        {...props.CurrencySelector}
                                        id={getDropdownId('retail')}
                                        value={props.CurrencySelector.DefaultCurrency}
                                        hideLabel
                                    />}
                                {props.PriceGroup && props.PriceGroup.length > 0 &&
                                    <div className='ry-detail__additional-listings'>
                                        <RentalAndLeasePrices PriceGroup={props.PriceGroup} IsRY={props.IsRY} ShowPriceEnteredAs={true} />
                                    </div>}
                                {props.RetailPrice &&
                                    <div className={'ry-listing-price__retail'}>
                                        <div>
                                            <span className='for-sale-price-title-detail'><strong><TranslationText tKey='Sandhills.ForSalePrice' />:</strong></span>
                                        </div>
                                        <div className='currency-selector'>
                                            {props.FormattedDiscountPrice ?
                                                <span className='listing-prices__discount'>
                                                    <span className='listing-prices__discount-retail-price'>{props.variant ? formatVariantPrice() : props.RetailPrice}</span>&nbsp;
                                                    <span className='listing-prices__discount-formatted-price'>{props.FormattedDiscountPrice}</span>
                                                </span>
                                                :
                                                <strong className='listing-prices__retail-price'>{props.DisplayPriceInclusiveTax && props.RetailPriceWithTax && !props.variant ? props.RetailPriceWithTax : props.variant ? formatVariantPrice() : props.RetailPrice}&nbsp;{props.RetailPricePerLabel}</strong>}
                                        </div>
                                        {props.RetailPriceEnteredAs &&
                                            <span className='listing-prices__price-entered-as'>
                                                (<TranslationText tKey='sandhills.tradepubwebsite.mvc.priceenteredas' />: {props.RetailPriceEnteredAs})
                                            </span>}
                                        {!props.variant && <>
                                            {props.RetailPriceWithTax && 
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.RetailPrice} + {props.TaxRateType} = {props.RetailPriceWithTax}</span>
                                                    <span className='listing-prices__tax-price'> ({props.TaxAppliesInTranslatedText})</span>
                                                </div>}
                                            {props.RetailPriceWithoutTax && 
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.RetailPriceWithoutTax} + {props.TaxRateType} = {props.RetailPrice}</span>
                                                    <span className='listing-prices__tax-price'> ({props.TaxAppliesInTranslatedText})</span>
                                                </div>}
                                            {props.TaxNotApplicable &&
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.TaxAppliesInTranslatedText}</span>
                                                </div>}
                                        </>}
                                    </div>}
                            </div>
                        </>
                        :
                        <>
                            {(props.RetailPrice || props.WholeSalePrice || props.AlernateRetailPrice) &&
                                <div className='listing-prices__main'>
                                    {(!props.ShowWholesalePriceOnly && props.ShowMSRPField && props.ShowMSRPLabel && props.MSRP) &&
                                        <div className='listing-prices__msrp'>
                                            <span className='listing-prices__msrp-label'>MSRP:</span>
                                            <span className='listing-prices__msrp-price'> {props.MSRP}</span>
                                        </div>}

                                    {((props.IsAuctionResult || props.IsPreATResult) && !props.ShowWholesalePriceOnly) &&
                                        <div className='listing-prices__auction-result-price'>
                                            <TranslationText tKey='sandhills.soldprice' />:
                                        </div>}

                                    {(props.ShowWholeSale && props.WholeSalePrice && props.IsExclusiveWholeSale && !props.IsPrivateParty) &&
                                        <div className='listing-prices__exclusive-wholesale'>
                                            <div className='currency-selector'>
                                                <CurrencySelector
                                                    {...props.CurrencySelector}
                                                    id={getDropdownId('exclusive')}
                                                    value={currencyCode}
                                                    onChange={(value) => setCurrencyCode(value)}
                                                    hideLabel
                                                />
                                                <span className='listing-prices__exclusive-wholesale-price'>
                                                    <TranslationText tKey='Sandhills.CRM.DSCRM.Wholesale' />: {props.WholeSalePrice}
                                                </span>
                                            </div>
                                        </div>}

                                    {!props.ShowWholesalePriceOnly &&
                                        <>
                                            {props.RetailPrice &&
                                                <div className={'listing-prices__retail'}>
                                                    <div className='currency-selector'>
                                                        {props.RetailPrice && !props.HideCurrencySelector &&
                                                            <CurrencySelector
                                                                {...props.CurrencySelector}
                                                                id={getDropdownId('retail')}
                                                                value={props.CurrencySelector.DefaultCurrency}
                                                                hideLabel
                                                            />}
                                                        {props.FormattedDiscountPrice ?
                                                            <span className='listing-prices__discount'>
                                                                <span className='listing-prices__discount-retail-price'>{props.variant ? formatVariantPrice() : props.RetailPrice}</span>&nbsp;
                                                                <span className='listing-prices__discount-formatted-price'>{props.FormattedDiscountPrice}</span>
                                                            </span>
                                                            :
                                                            <strong className='listing-prices__retail-price'>{props.DisplayPriceInclusiveTax && props.RetailPriceWithTax && !props.variant ? props.RetailPriceWithTax : props.variant ? formatVariantPrice() : props.RetailPrice}&nbsp;{props.RetailPricePerLabel}</strong>}
                                                    </div>
                                                </div>}

                                            {props.FormattedDiscountPrice ?
                                                <div className='listing-prices__discount'>
                                                    <span className='listing-prices__discount-amount'> {props.DiscountSaveAmount}</span>
                                                    <span className='listing-prices__discount-percentage'> {props.DiscountSavePercentage}</span>
                                                </div>
                                                :
                                                ((props.MSRPSavePercentage || props.MSRPSaveAmount) && props.ShowMSRPSaveLabel) &&
                                                <div className='listing-prices__msrp-save'>
                                                    {props.MSRPSaveAmount &&
                                                        <span className='listing-prices__msrp-save-amount'>{props.MSRPSaveAmount}
                                                        </span>}
                                                    {props.MSRPSavePercentage &&
                                                        <span className='listing-prices__msrp-save-percentage'>{props.MSRPSavePercentage}
                                                        </span>}
                                                </div>}
                                            {props.RetailPriceEnteredAs &&
                                                <span className='listing-prices__price-entered-as'>
                                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.priceenteredas' />: {props.RetailPriceEnteredAs}
                                                </span>}

                                            {props.RetailPriceWithTax && !props.variant &&
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.RetailPrice} + {props.TaxRateType} = {props.RetailPriceWithTax}</span>
                                                    <span className='listing-prices__tax-price'> ({props.TaxAppliesInTranslatedText})</span>
                                                </div>}
                                            {props.RetailPriceWithoutTax && !props.variant &&
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.RetailPriceWithoutTax} + {props.TaxRateType} = {props.RetailPrice}</span>
                                                    <span className='listing-prices__tax-price'> ({props.TaxAppliesInTranslatedText})</span>
                                                </div>}
                                            {props.TaxNotApplicable &&
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax-price'>{props.TaxAppliesInTranslatedText}</span>
                                                </div>}

                                            {props.AlernateRetailPrice &&
                                                <div className='listing-prices__alternate-retail'>
                                                    <div className='currency-selector'>
                                                        <CurrencySelector
                                                            {...props.CurrencySelector}
                                                            id={getDropdownId('alternate')}
                                                            value={props.CurrencySelector.DefaultCurrency}
                                                            hideLabel
                                                        />
                                                        <span className='listing-prices__alternate-retail-price'>{props.AlernateRetailPrice}&nbsp;{props.AlernateRetailPricePerLabel}</span>
                                                    </div>
                                                </div>}

                                            {props.AlternateRetailPriceEnteredAs &&
                                                <span className='listing-prices__price-entered-as'>
                                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.priceenteredas' />: {props.AlternateRetailPriceEnteredAs}
                                                </span>}

                                            {props.AlternateRetailPriceWithTax &&
                                                <div className='listing-prices__tax'>
                                                    <span className='listing-prices__tax'>{props.AlernateRetailPrice} + {props.TaxRateType} = {props.AlternateRetailPriceWithTax}</span>
                                                    <span className='listing-prices__tax'> ({props.TaxAppliesInTranslatedText})</span>
                                                </div>}
                                            {props.CalcPricePerHead &&
                                                <div className='sale'>
                                                    <strong><TranslationText tKey='Sandhills.CalculatedPricePerHead' />: </strong>{props.CalcPricePerHead}
                                                </div>}
                                        </>
                                    }
                                </div>}
                        </>
                    }

                    {hasAdditionalPricing() && !props.IsRY &&
                        <div className='listing-prices__additional'>
                            {props.ShowPreAuctionTime && !props.ShowWholesalePriceOnly &&
                                <div className='listing-prices__additional-price listing-prices__additional-price_pre-at'>
                                    <img className='listing-prices__additional-image' src={props.preATIcon} alt="" />
                                    <div className='listing-prices__additional-content'>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.preauctiontimeprice' />: <span className='no-wrap'>{props.WholeSalePrice}</span>
                                        <br />
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.toauction' />: <span className='no-wrap'>{props.PreAuctionTimeDate}</span>
                                    </div>
                                </div>}

                            {props.ShowWholeSale && !props.IsExclusiveWholeSale && (props.WholeSalePrice != props.RetailPrice) &&
                                <div className='listing-prices__additional-price listing-prices__additional-price_wholesale'>
                                    <img className='listing-prices__additional-image' src={props.ftiIcon} alt="" />
                                    <div className='listing-prices__additional-content'>
                                        <TranslationText tKey='sandhills.wholesaleprice' />: <span className='no-wrap'>{props.WholeSalePrice}</span>
                                    </div>

                                </div>}
                            {props.PriceGroup && props.PriceGroup.length > 0 &&
                                <RentalAndLeasePrices PriceGroup={props.PriceGroup} IsRY={props.IsRY} ShowPriceEnteredAs={true} />}
                        </div>}
                </>
            }
        </div >
    );
};

export default MainDetailPricing;